/**
 * Send Intake Email Modal
 *
 * Repurposes the former "Initialize Portal" no-op (attorney case detail,
 * empty Client Portal state) into an honest action: send the client their
 * intake email (upload + clarify links) via the proven send-unified-request
 * endpoint.
 *
 * IMPORTANT: this does NOT "initialize" the portal. `is_initialized` is
 * driven by the `document_requests` collection, created by a backend docs
 * pipeline, not by this one-click send. Sending this email will not flip
 * that flag, and the caller must not imply otherwise.
 */

import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Mail, Loader2 } from 'lucide-react';
import Modal from '../ui/Modal';
import { getAuthHeaders } from '../../lib/authHeaders';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface SendIntakeEmailModalProps {
  isOpen: boolean;
  onClose: () => void;
  caseId: string;
  clientName?: string;
  defaultEmail?: string;
}

export default function SendIntakeEmailModal({
  isOpen,
  onClose,
  caseId,
  clientName,
  defaultEmail,
}: SendIntakeEmailModalProps) {
  const [email, setEmail] = useState(defaultEmail || '');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset the field each time the modal is (re)opened, in case a different
  // case's default email is now in scope.
  useEffect(() => {
    if (isOpen) {
      setEmail(defaultEmail || '');
      setError(null);
    }
  }, [isOpen, defaultEmail]);

  const handleSend = async () => {
    const trimmed = email.trim();
    if (!trimmed || !EMAIL_RE.test(trimmed)) {
      setError('Enter a valid email address.');
      return;
    }

    setSending(true);
    setError(null);
    try {
      const authHeaders = await getAuthHeaders();
      const response = await fetch(
        `${BACKEND_URL}/api/v1/notifications/send-unified-request/${caseId}?recipient_email=${encodeURIComponent(trimmed)}`,
        {
          method: 'POST',
          headers: authHeaders,
        }
      );

      if (!response.ok) {
        let detail = 'Failed to send intake email';
        try {
          const data = await response.json();
          detail = data.detail || detail;
        } catch {
          // ignore parse failure, use default message
        }
        throw new Error(detail);
      }

      toast.success(`Intake email sent to ${trimmed}`);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to send intake email');
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Send Client Intake Email"
      size="sm"
      footer={
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={sending}
            className="px-4 py-2 rounded-lg font-medium transition-colors"
            style={{ backgroundColor: 'transparent', color: 'var(--color-text-muted)' }}
          >
            Cancel
          </button>
          <button
            onClick={handleSend}
            disabled={sending}
            className="px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2"
            style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)', opacity: sending ? 0.7 : 1 }}
          >
            {sending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Mail className="w-4 h-4" />
                Send Email
              </>
            )}
          </button>
        </div>
      }
    >
      <div className="space-y-3">
        <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
          Sends {clientName || 'the client'} their intake email with secure links to upload
          documents and categorize transactions. This does not initialize the client portal
          record — that is created automatically by the document intake pipeline once the
          client engages.
        </p>
        <div>
          <label
            htmlFor="intake-email-input"
            className="text-sm font-medium block mb-1"
            style={{ color: 'var(--color-text-secondary)' }}
          >
            Client Email
          </label>
          <input
            id="intake-email-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="client@example.com"
            disabled={sending}
            className="w-full px-3 py-2 rounded-lg text-sm"
            style={{ backgroundColor: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', color: 'var(--color-text-primary)' }}
          />
        </div>
        {error && (
          <p className="text-sm" style={{ color: 'var(--color-status-adverse)' }}>
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}
